/**
 * ページ送りを、ページ遷移せずその場の入れ替えにする
 *
 * @description 送り先のURLはサーバーが正しい中身を返すので、それをそのまま取ってきて
 *              一覧とページ送りの中身だけ差し替える。専用の返し口を作ると同じ
 *              マークアップをテンプレートと二重に持つことになるので作らない。
 *
 *              URLは変えない。タブの切り替えと同じ扱いで、再読み込みすると1ページ目へ戻る。
 */

import { armFade, disarmFade, fadeOut, fadeIn } from './utils-swapfade';

/** 差し替えのあいだ節に付ける印。クリックを止める */
const BUSY = 'is-loading';

/** 節そのものを指す印。テンプレート側が付ける */
const ROOT = '[data-listpager]';

/** 生きている取得。連打で追い越しが起きないよう前のを捨てる */
let live: AbortController | null = null;

/** 外したい手続き。非同期遷移で節ごと作り直すときに使う */
let detach: (() => void) | null = null;

/**
 * 節の中の一覧とページ送りを返す
 *
 * @description クラス名がテンプレートで違う（creator__ / artist__）ので、
 *              末尾で拾って名前の違いを吸収する
 * @param root 節
 * @returns 一覧とページ送り。揃っていなければ null
 */
const partsOf = (root: Element): { list: Element; pager: Element } | null => {
    const list = root.querySelector('[class$="topicsList"]');
    const pager = root.querySelector('[class$="topicsPager"]');

    return list && pager ? { list, pager } : null;
};

/**
 * 読み上げ用の領域を用意する
 *
 * @param root 節
 * @returns 知らせを書き込む要素
 */
const statusOf = (root: Element): HTMLElement => {
    const found = root.querySelector<HTMLElement>('[data-listpager-status]');

    if (found) {
        return found;
    }

    const el = document.createElement('p');

    el.setAttribute('data-listpager-status', '');
    el.setAttribute('role', 'status');
    el.setAttribute('aria-live', 'polite');
    el.className = 'listpagerStatus';
    root.appendChild(el);

    return el;
};

/**
 * 節の見出しを画面内へ戻す
 *
 * @description 押したページ送りは一覧の下端にあるので、入れ替えたあと見出しが
 *              画面の上に外れていることが多い。外れているときだけ戻す
 * @param root 節
 * @returns {void}
 */
const bringIntoView = (root: Element): void => {
    const top = root.getBoundingClientRect().top;

    if (top >= 0) {
        return;
    }

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    window.scrollTo({ top: window.scrollY + top, behavior: reduced ? 'auto' : 'smooth' });
};

/**
 * 押したものが差し替えで消えるので、焦点を置き直す
 *
 * @description 同じ役目の控えが残っていればそこへ、端に着いて押せなくなったときは
 *              ページ送りそのものへ寄せる。置き直さないと焦点がbodyへ落ちて、
 *              鍵盤の人はページの先頭から辿り直しになる
 * @param pager ページ送りの入れ物
 * @param want 押したものを指す目印。番号のように行き先が消えるものはnull
 * @returns {void}
 */
const refocus = (pager: Element, want: string | null): void => {
    const again = want ? pager.querySelector<HTMLElement>(want) : null;

    if (again) {
        again.focus();

        return;
    }

    const nav = pager.querySelector<HTMLElement>('.Pager');

    if (!nav) {
        return;
    }

    nav.tabIndex = -1;
    nav.focus();
};

/**
 * 送り先を取ってきて中身を差し替える
 *
 * @param root 節
 * @param href 送り先
 * @param from 押したものを指す目印
 * @returns {void}
 */
const swap = async (root: HTMLElement, href: string, from: string | null): Promise<void> => {
    const parts = partsOf(root);

    if (!parts) {
        return;
    }

    live?.abort();
    live = new AbortController();

    const mine = live;

    root.classList.add(BUSY);

    // 伏せるのと取りに行くのは同時に始める。待ち時間を足さないため
    const hidden = fadeOut(parts.list);

    try {
        const res = await fetch(href, { signal: mine.signal, headers: { 'X-Requested-With': 'fetch' } });

        if (!res.ok) {
            throw new Error(String(res.status));
        }

        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
        const next = doc.querySelector(ROOT);
        const nextParts = next ? partsOf(next) : null;

        if (!nextParts) {
            throw new Error('差し替える中身が見つからない');
        }

        // 伏せ切る前に差し替えると、入れ替わる瞬間が見えてしまう
        await hidden;

        // 文字列へ戻さず、解析済みのノードをそのまま移す
        parts.list.replaceChildren(...Array.from(nextParts.list.childNodes, (n) => document.importNode(n, true)));
        parts.pager.replaceChildren(...Array.from(nextParts.pager.childNodes, (n) => document.importNode(n, true)));

        const now = parts.pager.querySelector('[aria-current="page"]')?.textContent?.trim() ?? '';

        statusOf(root).textContent = now !== '' ? `${now}ページ目を表示しました` : '一覧を切り替えました';

        bringIntoView(root);
        refocus(parts.pager, from);
        fadeIn(parts.list);
    } catch (err) {
        if (mine.signal.aborted) {
            return;
        }

        // 取れなかったときは黙って止めず、ふつうの遷移で送り先を見せる
        window.location.href = href;

        return;
    } finally {
        if (!mine.signal.aborted) {
            root.classList.remove(BUSY);
        }
    }
};

/**
 * その場の入れ替えを止める
 *
 * @returns {void}
 */
const destroyListPager = (): void => {
    live?.abort();
    live = null;
    detach?.();
    detach = null;
};

/**
 * その場の入れ替えを始める
 *
 * @returns {void}
 */
const initListPager = (): void => {
    destroyListPager();

    const root = document.querySelector<HTMLElement>(ROOT);
    const parts = root ? partsOf(root) : null;

    if (!root || !parts) {
        return;
    }

    armFade(parts.list);

    /**
     * ページ送りのリンクを横取りする
     *
     * @param e クリック
     * @returns {void}
     */
    const onClick = (e: MouseEvent): void => {
        // 別タブで開く操作や中クリックは、押した人の意図どおりに通す
        if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
            return;
        }

        const link = (e.target as Element | null)?.closest<HTMLAnchorElement>('[class$="topicsPager"] a[href]');

        if (!link || !root.contains(link)) {
            return;
        }

        e.preventDefault();

        if (root.classList.contains(BUSY)) {
            return;
        }

        // 矢印は同じ向きの控えへ戻す。番号は現在地のspanに変わって消えるので寄せ先が無い
        const dir = link.classList.contains('attr-prev') ? 'prev' : link.classList.contains('attr-next') ? 'next' : null;

        void swap(root, link.href, dir ? `a.Pager__nav.attr-${dir}` : null);
    };

    root.addEventListener('click', onClick);

    detach = () => {
        root.removeEventListener('click', onClick);
        disarmFade(parts.list);
    };
};

export { initListPager, destroyListPager };

document.addEventListener('DOMContentLoaded', initListPager);
